// =============================================================================
// AI Portal page — shell hosting two in-page sub-tabs:
//   "AI Chat"     → conversational Q&A over live data (AskPortalChat, R39/40/53)
//   "AI Charting" → natural-language charting on demand (AskPortalChart, R42)
//
// Mirrors the Dashboard page's shell + subfolder pattern (Dashboard.tsx +
// pages/dashboard/*). The shell owns the header + tab strip; each sub-tab is a
// self-contained component in pages/askportal/.
// =============================================================================
import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { SectionTitle } from '../components/ui'
import { AskPortalChat } from './askportal/AskPortalChat'
import { AskPortalChart } from './askportal/AskPortalChart'

type Tab = 'chat' | 'chart'

const TABS: { key: Tab; label: string }[] = [
  { key: 'chat', label: 'AI Chat' },
  { key: 'chart', label: 'AI Charting' },
]

export function AskPortalPage() {
  const [tab, setTab] = useState<Tab>('chat')

  return (
    // Fill the content area exactly (h-full, not a 100vh calc that ignores the top
    // persona bar + padding and left a few px of page scroll) so the page itself never
    // scrolls — the title and tab strip stay put and each sub-tab scrolls only its own
    // inner region. The content area has a definite height (Layout: main flex-1 → header
    // + this), so h-full resolves cleanly.
    <div className="flex h-full flex-col">
      <div className="flex-shrink-0">
        <SectionTitle sub="Ask questions or build charts in plain English — answers come from live data">
          <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand-600" /> AI Portal</span>
        </SectionTitle>
      </div>

      {/* Sub-tab strip — underlined active tab, matching the Dashboard page */}
      <div className="mt-2 flex flex-shrink-0 items-center gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ${
              tab === t.key
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        {tab === 'chat' ? <AskPortalChat /> : <AskPortalChart />}
      </div>
    </div>
  )
}
